<template>
  <div class="lightbox-filters">
    <div class="controls-container">
      <div
        class="btn-container -inline"
        ref="filterContainer"
        :title="selectedAssetsFilter ? 'Filter Active' : 'Filter'"
      >
        <div
          class="-button -filter-label"
          :class="{ active: !!selectedAssetsFilter, opened: filterBoxOpen }"
          @click="toggleFilterBox"
        >
          <i class="wticon wticon-filter"></i>
        </div>
      </div>
      <div
        class="btn-container -inline"
        ref="searchContainer"
        :title="searchQuery ? 'Search Active' : 'Search'"
      >
        <div
          class="-button -filter-label"
          :class="{ active: searchQuery, opened: isSearchBoxOpen }"
          @click="toggleSearch"
        >
          <i class="wticon wticon-magnifier"></i>
          <!-- Green dot indicator when search is active but box is closed -->
          <!-- <span v-if="searchQuery" class="active-filter-dot"></span> -->
        </div>
      </div>
    </div>

    <ul v-if="filterBoxOpen" class="-btn-dropdown-container" ref="filterBox">
      <li
        v-for="button in buttons"
        :title="'Filter by ' + button.text"
        :key="button.value"
        @click="changeFilter(button.value)"
        :class="{ '-active': selectedAssetsFilter === button.value }"
      >
        <i class="wticon" :class="button.icon"></i>
        {{ button.text }}
      </li>
    </ul>

    <div v-if="isSearchBoxOpen" class="search-box" ref="searchBox">
      <label for="search-input" class="input-label" v-show="!searchQuery"
        >Search...</label
      >
      <input
        ref="searchInput"
        type="text"
        v-model="searchQuery"
        @input="handleSearch"
      />
      <div
        v-show="searchQuery"
        class="clear-button wt2-link"
        @click="clearSearch"
        title="Clear search"
        ref="clearButton"
      >
        Clear
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'LightboxFilters',
  props: {
    selectedAssetsFilter: {
      type: [String, null],
      default: null,
    },
  },
  data() {
    return {
      buttons: [
        {
          text: 'Videos',
          title: 'Videos',
          icon: 'wticon-camera',
          value: 'VIDEO',
        },
        {
          text: 'Images',
          title: 'Images',
          icon: 'wticon-picture-sun',
          value: 'IMAGE',
        },
        { text: 'Audio', title: 'Audio', icon: 'wticon-mic', value: 'AUDIO' },
        {
          text: 'Uploads',
          title: 'Uploads',
          icon: 'wticon-upload2',
          value: 'UPLOADS',
        },
      ],
      isSearchBoxOpen: false,
      searchQuery: '',
      filterBoxOpen: false,
    };
  },
  mounted() {
    // Add click outside listener
    document.addEventListener('click', this.handleClickOutside);
  },
  beforeDestroy() {
    // Remove event listener when component is destroyed
    document.removeEventListener('click', this.handleClickOutside);
  },
  methods: {
    changeFilter(newFilter) {
      let selectedFilter = newFilter || null;
      if (newFilter === this.selectedAssetsFilter) {
        selectedFilter = null;
      }
      this.$emit('update:selectedAssetsFilter', selectedFilter);
    },
    toggleFilterBox() {
      this.filterBoxOpen = !this.filterBoxOpen;
    },
    toggleSearch() {
      // If search is active and closed, clicking should just open the search box
      if (this.searchQuery && !this.isSearchBoxOpen) {
        this.isSearchBoxOpen = true;
        this.$nextTick(() => {
          this.$refs.searchInput.focus();
        });
        return;
      }

      this.isSearchBoxOpen = !this.isSearchBoxOpen;

      if (this.isSearchBoxOpen) {
        // Focus the input when search box is opened
        this.$nextTick(() => {
          this.$refs.searchInput.focus();
        });
      }
    },
    handleSearch() {
      // Emit search event for parent component to filter assets
      this.$emit('search', this.searchQuery);
    },
    clearSearch() {
      this.searchQuery = '';
      this.handleSearch();
      // Focus back on input after clearing
      if (this.isSearchBoxOpen) {
        this.$refs.searchInput.focus();
      }
    },
    handleClickOutside(event) {
      // Check if click is outside both the search container and search box
      if (
        this.$refs.searchContainer &&
        this.$refs.searchBox &&
        !this.$refs.searchContainer.contains(event.target) &&
        !this.$refs.searchBox.contains(event.target) &&
        !this.$refs.searchInput.contains(event.target) &&
        !this.$refs.clearButton.contains(event.target)
      ) {
        // Close the search box but keep the filter active if there's a query
        this.isSearchBoxOpen = false;
      }

      if (
        this.$refs.filterBox &&
        !this.$refs.filterBox.contains(event.target) &&
        !this.$refs.filterContainer.contains(event.target)
      ) {
        this.filterBoxOpen = false;
      }
    },
  },
  watch: {
    // Watch for search query changes and emit event
    searchQuery(newVal) {
      this.$emit('search', newVal);
    },
  },
};
</script>

<style lang="scss">
.lightbox-filters {
  display: flex;
  flex-direction: column;
  align-items: flex-end;

  .controls-container {
    display: flex;
    align-items: center;
    // width: 181px;
  }

  .-format-selector {
    background: rgba($color-white, 0.95);
    transition: background 0.2s;
    // width: 155px;

    &:hover {
      background: rgba($color-white, 1);
    }
  }

  .wt2-radio-text.-format-selector {
    .-button:last-child {
    }
  }

  .btn-container {
    position: relative;
    height: 30px;

    flex: 1;
    display: inline-flex;
    flex-grow: 0;
    &:first-child {
      .-button {
        border-left: 1px solid rgba(0, 0, 0, 0.2);
      }
    }
  }

  .-button {
    position: relative;
    display: flex;
    justify-content: center;
    align-items: center;
    border: 1px solid rgba(0, 0, 0, 0.2);
    background: rgba($color-white, 1);
    border-left: none;
    cursor: pointer;
    transition: all 0.2s;
    width: 100%;
    font-size: 16px;
    font-weight: 400;
    transition: color 0.2s;
    line-height: 0;
    width: 30px;

    &:hover,
    &.opened {
      background: rgba($color-white, 1);
      color: #06a9f4;
    }
    &.active {
      // border-left: 1px solid #06a9f4;
      // border: none;
      color: white;
      background-color: #06a9f4;
    }
  }

  .active-filter-dot {
    position: absolute;
    top: -3px;
    right: -3px;
    width: 12px;
    height: 12px;
    background-color: #20bc9c;
    border: 1px solid white;
    border-radius: 50%;
    box-shadow: 1px 1px 4px 0 rgba(0, 0, 0, 0.5);
  }

  .search-box {
    margin-top: 10px;
    background: white;
    border-radius: 4px;
    box-shadow: 0 2px 10px rgba(0, 0, 0, 0.25);
    display: flex;
    align-items: center;
    padding: 0 8px;
    z-index: 100;
    width: 100%;
    position: absolute;
    right: 0;
    bottom: 0;
    transform: translateY(110%);
  }

  .search-box input {
    border: none;
    outline: none;
    padding: 8px 10px 8px 4px;
    width: 100%;
  }

  .input-label {
    position: absolute;
    font-size: 14px;
    font-weight: 400;
    left: 15px;
    color: #999;
  }

  .clear-button {
    background: none;
    border: none;
    cursor: pointer;
    font-size: 12px;
    padding-top: 2px;
  }

  .-btn-dropdown-container {
    list-style: none;
    padding: 0;
    margin: 0;
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    justify-content: flex-start;
    background: white;
    border-radius: 4px;
    box-shadow: 0 2px 10px rgba(0, 0, 0, 0.25);
    z-index: 100;
    position: absolute;
    top: 0;
    width: 120px;
    transform: translateY(33px) translateX(50%);
    li {
      padding: 10px 12px;
      width: 100%;
      cursor: pointer;
      .wticon {
        padding-right: 7px;
        vertical-align: text-bottom;
      }
      &:hover {
        background: #f0f0f0;
      }
      &.-active {
        color: #06a9f4;
      }
    }
  }
}
</style>
